import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardContent,
  Grid,
  Zoom,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../../helpers/formatCurrency';

const TransactionDisplay = ({ isBulkMode, currentTransactions, selectedStatement }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BankIcon sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="h6" fontWeight="600" color="primary">
            {isBulkMode ? 'Extratos Bancários' : 'Extrato Bancário'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isBulkMode
              ? `${currentTransactions.length} extratos selecionados`
              : 'Dados originais do extrato bancário'}
          </Typography>
        </Box>
        <Tooltip title="Estes são os extratos bancários que servirão de base para criar os lançamentos no Vita">
          <InfoIcon color="action" fontSize="small" />
        </Tooltip>
      </Box>

      {isBulkMode ? (
        // Modo bulk: mostrar lista de transações
        <Box
          sx={{
            maxHeight: 280,
            overflow: 'auto',
            borderRadius: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0,0,0,0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(0,0,0,0.3)',
              },
            },
          }}
        >
          <Stack spacing={1.5}>
            {currentTransactions.map((txn, index) => (
              <Zoom in key={txn.transactionHash} style={{ transitionDelay: `${index * 50}ms` }}>
                <Card
                  elevation={1}
                  sx={{
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={7}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 40 }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {txn.description}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(txn.datePosted).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={5}
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        gap={1}
                      >
                        <Chip
                          icon={txn.amount > 0 ? <IncomeIcon /> : <ExpenseIcon />}
                          label={formatCurrency(Math.abs(txn.amount))}
                          color={txn.amount > 0 ? 'success' : 'error'}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Zoom>
            ))}
          </Stack>
        </Box>
      ) : (
        // Modo single: mostrar uma transação
        <Card elevation={2}>
          <CardContent sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <BankIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {selectedStatement?.bankName || 'Banco'}
                  </Typography>
                </Box>

                <Typography variant="h6" fontWeight="600" mb={1.5}>
                  {currentTransactions[0].description}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Data
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {new Date(currentTransactions[0].datePosted).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary" display="block">
                          Valor
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="600"
                          color={currentTransactions[0].amount > 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(Math.abs(currentTransactions[0].amount))}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="center">
                  <Chip
                    icon={currentTransactions[0].amount > 0 ? <IncomeIcon /> : <ExpenseIcon />}
                    label={currentTransactions[0].amount > 0 ? 'Entrada' : 'Saída'}
                    color={currentTransactions[0].amount > 0 ? 'success' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Paper>
  );
};

export default TransactionDisplay;
